import React, { PropsWithChildren, ReactNode, useMemo } from 'react'

import { Grid, Masthead, MastheadProps } from '@barracuda-internal/bds-core-v2'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { useFormatMessage } from 'lib/localization'
import { useBlockTransition } from 'lib/useBlockTransition'

import ContentLoader from 'components/libs/contentLoader/ContentLoader'
import DomainConflictDialog from 'components/libs/domainConflictDialog/DomainConflictDialog'
import { useSettingsStateLogic } from 'components/libs/useSettingsState'

const DIALOG_I18N_KEY = 'ess.dialog'
const PRODUCT_TITLE = 'Email Gateway Defense'

export type PageContainerV2Props = {
  isLoading?: boolean
  isFailedToLoad?: boolean
  dirtyCheck?: boolean
  isDirtyForm?: boolean
  masthead?: MastheadProps
  includeSettingsLogic?: boolean
  includeDomainConflict?: boolean
}

export const PageContainerV2 = ({
  children,
  isLoading = false,
  isFailedToLoad = false,
  dirtyCheck = false,
  isDirtyForm = false,
  masthead,
  includeSettingsLogic = false,
  includeDomainConflict = false
}: PropsWithChildren<PageContainerV2Props>) => {
  const formatDialogText = useFormatMessage(DIALOG_I18N_KEY)
  const [transitionCb, resetTransitionCb] = useBlockTransition({ dirtyCheck, isDirtyForm })

  const [UseSettingsStateLogic] = useSettingsStateLogic()

  const finalIsLoading = useMemo(() => {
    if (includeSettingsLogic && UseSettingsStateLogic) return UseSettingsStateLogic.isLoading
    return isLoading
  }, [includeSettingsLogic, UseSettingsStateLogic, isLoading])

  // Determine failed state
  const finalIsFailedToLoad = useMemo(() => {
    if (includeSettingsLogic && UseSettingsStateLogic) return UseSettingsStateLogic.isFailedToLoad
    return isFailedToLoad
  }, [includeSettingsLogic, UseSettingsStateLogic, isFailedToLoad])

  return useMemo(
    () => (
      <ContentLoader isLoading={finalIsLoading} isFailed={finalIsFailedToLoad} fullPage>
        <Grid container direction="column">
          {masthead && (
            <Grid item>
              <Masthead {...masthead} productTitle={masthead.productTitle || PRODUCT_TITLE} />
            </Grid>
          )}
          <Grid item sx={{ padding: 3 }}>
            {children}
          </Grid>
        </Grid>

        {!!transitionCb && (
          <WarningDialog
            title={formatDialogText('settings_page_container.title')}
            description={formatDialogText('settings_page_container.text')}
            onCancel={resetTransitionCb}
            onConfirm={transitionCb}
          />
        )}

        {includeDomainConflict && <DomainConflictDialog />}
      </ContentLoader>
    ),
    [
      finalIsLoading,
      finalIsFailedToLoad,
      masthead,
      children,
      transitionCb,
      formatDialogText,
      resetTransitionCb,
      includeDomainConflict
    ]
  )
}
